import { useState, useCallback, useEffect } from 'react';

/**
 * @typedef {{ kind: 'offline'|'rate_limit'|'mic_permission_denied'|'mic_not_supported'|'mic_hardware_error'|'network', retryable: boolean, meta?: { retryAfterSec?: number, originalMessage?: string } }} ErrorState
 */

export default function useAiAssistantErrors() {
  const [errorState, setErrorState] = useState(null);

  const setError = useCallback((error) => {
    setErrorState((prev) => {
      // Offline is sticky — don't overwrite with another error while still offline
      if (prev?.kind === 'offline' && !navigator.onLine) return prev;
      return error;
    });
  }, []);

  const clearError = useCallback(() => setErrorState(null), []);

  const setErrorFromException = useCallback((err, fallbackKind = 'network') => {
    const msg = err?.message || String(err ?? '');
    if (err?.code === 'AI_NOT_ENABLED') {
      setError({ kind: 'ai_not_enabled', retryable: false, meta: { originalMessage: msg } });
      return;
    }
    if (err?.code === 'AI_LICENSE_BLOCKED') {
      setError({ kind: 'ai_license_blocked', retryable: false, meta: { originalMessage: msg } });
      return;
    }
    if (err?.code === 'AI_USAGE_CAP_REACHED') {
      setError({ kind: 'ai_usage_cap_reached', retryable: false, meta: { originalMessage: msg } });
      return;
    }
    if (err?.status === 429 || /rate[_\s-]?limit|too many requests/i.test(msg)) {
      const retryAfterSec = err?.retryAfterSec ?? 60;
      setError({ kind: 'rate_limit', retryable: true, meta: { retryAfterSec } });
      return;
    }
    if (!navigator.onLine) {
      setError({ kind: 'offline', retryable: false });
      return;
    }
    // A6: auth_required maps to the generic network banner (session expired)
    if (msg === 'auth_required') {
      setError({ kind: 'network', retryable: true, meta: { originalMessage: msg } });
      return;
    }
    setError({ kind: fallbackKind, retryable: true, meta: { originalMessage: msg } });
  }, [setError]);

  // Auto-detect offline via browser events
  useEffect(() => {
    const onOffline = () => setErrorState({ kind: 'offline', retryable: false });
    const onOnline = () => setErrorState((prev) => (prev?.kind === 'offline' ? null : prev));
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    if (!navigator.onLine) onOffline();
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  return { errorState, setError, clearError, setErrorFromException };
}
